import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Key,
  Shield,
  Share2,
  Settings,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Inbox,
  Lock,
  FolderLock,
  BookOpen
} from 'lucide-react';

const Sidebar = ({ isOpen, onToggle, onClose, onMenuAction }) => {
  const location = useLocation();
  
  const menuItems = [
    { 
      id: 'passwords', 
      label: '패스워드', 
      icon: Key, 
      path: '/',
      description: '패스워드 관리',
      action: 'navigate'
    },
    { 
      id: 'secure-notes', 
      label: '보안 메모장', 
      icon: Lock, 
      path: '/secure-notes',
      description: 'PIN 보호 메모 관리',
      action: 'navigate'
    },
    {
      id: 'secure-files',
      label: '파일 보관소',
      icon: FolderLock,
      path: '/secure-files',
      description: '암호화된 파일 보관',
      action: 'navigate'
    },
    {
      id: 'knowledge',
      label: 'AI 어시스턴트',
      icon: BookOpen,
      path: '/knowledge',
      description: 'AI와 대화로 패스워드 관리',
      action: 'navigate'
    },
    {
      id: 'sharing',
      label: '공유한 패스워드',
      icon: Share2,
      path: '/share-management',
      description: '공유한 패스워드 관리',
      action: 'navigate'
    },
    { 
      id: 'shared-passwords', 
      label: '공유받은 패스워드', 
      icon: Inbox, 
      path: '/shared-passwords',
      description: '공유받은 패스워드 관리',
      action: 'navigate'
    },
    { 
      id: 'security', 
      label: '보안 분석', 
      icon: Shield, 
      path: '/security',
      description: '보안 분석 및 권장사항',
      action: 'modal'
    },
    { 
      id: 'settings', 
      label: '설정', 
      icon: Settings, 
      path: '/settings',
      description: '시스템 설정',
      action: 'navigate'
    }
  ];

  const isActiveRoute = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* 모바일 오버레이 - 768px 이하에서만 표시 */}
      {isOpen && window.innerWidth <= 768 && (
        <div 
          className="sidebar-overlay"
          onClick={onClose}
        />
      )}
      
      {/* 사이드바 */}
      <aside className={`sidebar ${isOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* 사이드바 헤더 */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            {isOpen ? (
              <>
                <div className="brand-icon">🔐</div>
                <div className="brand-text">
                  <h2>Password Wallet</h2>
                  <p>보안 패스워드 관리</p>
                </div>
              </>
            ) : (
              /* 접혔을 때는 펼치기 버튼을 헤더에 표시 */
              <button
                className="sidebar-toggle-collapsed"
                onClick={onToggle}
                title="사이드바 펼치기"
              >
                <ChevronRight size={20} />
              </button>
            )}
          </div>
          
          {/* 토글 버튼 - 열려있을 때만 표시 */}
          {isOpen && (
            <button
              className="sidebar-toggle"
              onClick={onToggle}
              title="사이드바 접기"
            >
              <ChevronLeft size={18} />
            </button>
          )}
        </div>

        {/* 네비게이션 메뉴 */}
        <nav className="sidebar-nav">
          <ul className="nav-menu">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.path);
              
              return (
                <li key={item.id} className="nav-item">
                  <a
                    href={item.path}
                    className={`nav-link ${isActive ? 'nav-link-active' : ''}`}
                    title={!isOpen ? item.label : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      
                      // 메뉴 액션 처리
                      if (item.action === 'modal') {
                        // 모달 오픈 액션을 상위 컴포넌트로 전달
                        onMenuAction && onMenuAction(item.id);
                      } else if (item.action === 'navigate') {
                        // 네비게이션 처리
                        if (item.id === 'passwords') {
                          // 패스워드 메뉴 클릭 시 메인 대시보드로 이동
                          window.location.href = item.path;
                        } else if (item.id === 'secure-notes') {
                          // 보안 메모장 페이지로 이동
                          window.location.href = item.path;
                        } else if (item.id === 'secure-files') {
                          // 파일 보관소 페이지로 이동
                          window.location.href = item.path;
                        } else if (item.id === 'knowledge') {
                          // 지식 베이스 페이지로 이동
                          window.location.href = item.path;
                        } else if (item.id === 'shared-passwords') {
                          // 공유받은 패스워드 페이지로 이동
                          window.location.href = item.path;
                        } else if (item.id === 'sharing') {
                          // 공유 관리 페이지로 이동
                          window.location.href = item.path;
                        } else if (item.id === 'settings') {
                          // 설정 페이지로 이동
                          window.location.href = item.path;
                        }
                      }
                      
                      // 모바일에서는 메뉴 선택 시 사이드바 닫기
                      if (window.innerWidth <= 768) {
                        onClose();
                      }
                    }}
                  >
                    <Icon size={20} className="nav-icon" />
                    {isOpen && (
                      <div className="nav-content">
                        <span className="nav-label">{item.label}</span>
                        <span className="nav-description">{item.description}</span>
                      </div>
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* 사이드바 푸터 */}
        {isOpen && (
          <div className="sidebar-footer">
            <div className="footer-info">
              <p className="footer-version">v2.0.0</p>
              <p className="footer-copyright">© 2025 Password Wallet</p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;